// Defensive fetch for the /api/claude-substitute endpoint.
// Reads the response body as text first, then attempts JSON.parse — so
// empty bodies, HTML error pages, and platform timeouts produce a clear
// error message instead of "Unexpected end of JSON input".

export type SubstituteResult = {
  role:        string
  substitutes: Array<{ name: string; ratio: string; note: string }>
}

export async function fetchSubstitutes(params: {
  ingredient:     string
  recipeName:     string
  allIngredients: string[]
}): Promise<SubstituteResult> {
  const res = await fetch('/api/claude-substitute', {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify(params),
  })

  // Always read body as text first — it can be empty, HTML (Vercel error page),
  // or JSON. This is what keeps r.json() from crashing on non-JSON bodies.
  const raw = await res.text()

  if (!res.ok) {
    let message = `Substitute lookup failed (${res.status})`
    try {
      const parsed = JSON.parse(raw)
      if (parsed?.error) message = parsed.error
    } catch {
      // body wasn't JSON — keep the generic status-coded message
    }
    throw new Error(message)
  }

  if (!raw) {
    throw new Error('Substitute service returned no data. Try again in a moment.')
  }

  try {
    const parsed = JSON.parse(raw) as SubstituteResult
    if (!parsed || !Array.isArray(parsed.substitutes)) {
      throw new Error('Substitute service returned invalid data. Try again in a moment.')
    }
    return parsed
  } catch (err) {
    if (err instanceof Error && err.message.startsWith('Substitute service')) throw err
    throw new Error('Substitute service returned invalid data. Try again in a moment.')
  }
}
